import React, {useEffect, useRef} from 'react';
import {Notification} from '../navigation/page-layout';
import {RegionId, ServiceResource, useAuth} from '../hooks/use-auth';
import {translateErrorToReactNode} from '../common';
import PortalLoader from '../common/PortalLoader';
import * as QuickSightEmbedding from 'amazon-quicksight-embedding-sdk';
import {AppLayout, Button, ContentLayout, Header, HelpPanel, Link} from '@amzn/awsui-components-react';
import {useGetDashboardUrlByDashboardName} from '../hooks/use-quicksight-api';
import {useRouter} from '../hooks/use-router';

export function ViewOteAnomalies(props: {pushNotification: (notification: Notification) => void}) {
    const auth = useAuth();
    const router = useRouter();
    const selectedRegion = RegionId.EU;

    const clientConfigs = auth.authInformation!.getEndpoints()[selectedRegion][ServiceResource.ForecastStoreInsights];

    function createErrorListener(header: string) {
        return (e: any) => {
            props.pushNotification({
                type: 'error',
                content: translateErrorToReactNode(e),
                header,
            });
        };
    }

    const {status: getDashboardUrlStatus, value: getDashboardUrlResponse} = useGetDashboardUrlByDashboardName(
        clientConfigs,
        {dashboardName: 'OTE Anomaly Detection'},
        true,
        createErrorListener('GetDashboardUrlByDashboardName failed'),
        [auth, selectedRegion]
    );

    const {value: keptDashboardResponse} = useGetDashboardUrlByDashboardName(
        clientConfigs,
        {dashboardName: 'OTE Anomaly Kept'},
        true,
        createErrorListener('Unable to load kept OTE anomalies'),
        [auth, selectedRegion]
    );

    const isLoading = getDashboardUrlStatus === 'idle' || getDashboardUrlStatus === 'pending';
    const keptUrl = keptDashboardResponse?.dashboardUrl || '';

    return (
        <AppLayout
            disableContentPaddings={true}
            content={
                <ContentLayout>
                    {isLoading ? (
                        <PortalLoader />
                    ) : (
                        <>
                            <OteAnomalyDashboard
                                key={selectedRegion}
                                url={getDashboardUrlResponse?.dashboardUrl || ''}
                                pushNotification={props.pushNotification}
                            />
                            <div style={{textAlign: 'center', padding: '2rem 1vw'}}>
                                <Header variant={'h2'}>Manage Anomaly Exclusions</Header>
                                <p
                                    style={{
                                        fontFamily: "'Amazon Ember', 'Helvetica Neue', Roboto, Arial, sans-serif",
                                    }}
                                >
                                    Download the exclusion template, mark anomalies to exclude, and upload the completed file from
                                    Input Signals.
                                </p>
                                <Button variant="primary" onClick={() => router.push('/signals')}>
                                    Click here to manage exclusions in Input Signals
                                </Button>
                            </div>
                            {keptUrl ? (
                                <div style={{height: 760, width: '100%'}}>
                                    <OteAnomalyDashboard
                                        key={`kept-${selectedRegion}`}
                                        url={keptUrl}
                                        pushNotification={props.pushNotification}
                                        useEmbeddingContainerId={false}
                                    />
                                </div>
                            ) : (
                                <p style={{textAlign: 'center'}}>Kept anomalies will appear here once the dashboard is deployed.</p>
                            )}
                        </>
                    )}
                </ContentLayout>
            }
            tools={
                <HelpPanel header="OTE Anomaly Detection">
                    <p>
                        <Link
                            href="https://quip-amazon.com/OSkNAy4YvCuI/OTE-Exclusions-for-QC-MENA-NA"
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            OTE Exclusions Design Doc
                        </Link>
                    </p>
                    <p>
                        <Link
                            href="https://quip-amazon.com/FFWjA53hrK3L/OTE-Exclusions-for-MENA-NA-Frontend-Implementation-REVISED"
                            external
                            externalIconAriaLabel="Opens in a new tab"
                        >
                            Frontend Implementation (Revised)
                        </Link>
                    </p>
                </HelpPanel>
            }
            navigationHide
        />
    );
}

interface OteAnomalyDashboardProps {
    url: string;
    pushNotification: (notification: Notification) => void;
    // When false, the embed fills its parent via an inline height instead of the shared
    // #embeddingContainer id, so a second stacked embed can render without duplicating the id.
    useEmbeddingContainerId?: boolean;
}

function OteAnomalyDashboard(props: OteAnomalyDashboardProps) {
    const ref = useRef<HTMLDivElement | null>(null);
    const useEmbeddingContainerId = props.useEmbeddingContainerId ?? true;

    function onError(error: any) {
        props.pushNotification({
            type: 'error',
            header: 'Embedding Quicksight Dashboard failed',
            content: translateErrorToReactNode(error),
        });
    }

    useEffect(() => {
        if (!ref.current || !props.url) return;
        const dashboard = QuickSightEmbedding.embedDashboard({
            url: props.url,
            container: ref.current,
            locale: 'en-US',
            footerPaddingEnabled: true,
            scrolling: 'yes',
            parameters: {},
        });
        dashboard.on('error', onError);
    }, []);

    // Primary embed keeps id="embeddingContainer" (matches the shipped, clean-rendering page).
    // The kept embed instead fills its fixed-height wrapper via inline height:100% — same
    // "no height passed to the SDK" behavior, just without a duplicate element id.
    if (useEmbeddingContainerId) {
        return <div id="embeddingContainer" ref={ref} />;
    }
    return <div ref={ref} style={{height: '100%', width: '100%'}} />;
}
